import { cn } from "@/lib/utils";
import { forwardRef } from "react";

// ============================================
// 타입 정의
// ============================================

type ButtonVariant = "solid" | "outlined";
type ButtonColor = "primary" | "secondary" | "blue" | "negative";
export type ButtonSize =
  | "xlarge"
  | "large"
  | "base"
  | "small"
  | 48
  | 40
  | 36
  | 32
  | 28
  | 24;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

// ============================================
// 스타일 정의
// ============================================

const baseStyles =
  "group/button relative inline-flex items-center justify-center transition-colors overflow-hidden";

// Figma 사이즈별 padding-x · gap · radius (variable defs 기반)
// 48px → px=12 gap=6 radius=8px
// 40px → px=12 gap=4 radius=6px
// 36px → px=10 gap=4 radius=6px
// 32px → px=10 gap=4 radius=6px
// 28px → px=8  gap=4 radius=6px
// 24px → px=8  gap=4 radius=6px
const sizeStyles: Record<ButtonSize, string> = {
  xlarge: "h-12 px-3 gap-1.5 rounded-[8px]", // = 48px
  large: "h-10 px-3 gap-1 rounded-small", // = 40px
  base: "h-9 px-2.5 gap-1 rounded-small", // = 36px
  small: "h-7 px-2 gap-1 rounded-small", // = 28px
  48: "h-12 px-3 gap-1.5 rounded-[8px]",
  40: "h-10 px-3 gap-1 rounded-small",
  36: "h-9 px-2.5 gap-1 rounded-small",
  32: "h-8 px-2.5 gap-1 rounded-small",
  28: "h-7 px-2 gap-1 rounded-small",
  24: "h-6 px-2 gap-1 rounded-small",
};

const textStyles: Record<ButtonSize, string> = {
  xlarge: "headline-2-medium",
  large: "body-2-normal-medium",
  base: "body-2-normal-medium",
  small: "label-2-medium",
  48: "headline-2-medium",
  40: "body-2-normal-medium",
  36: "body-2-normal-medium",
  32: "label-1-normal-medium",
  28: "label-2-medium",
  24: "caption-2-medium",
};

// Figma 사이즈별 icon 크기:
// 48px → 24, 40px → 24, 36px → 20, 32px → 18, 28px → 16, 24px → 14
const iconSizeStyles: Record<ButtonSize, string> = {
  xlarge: "size-6",
  large: "size-6",
  base: "size-5",
  small: "size-4",
  48: "size-6",
  40: "size-6",
  36: "size-5",
  32: "size-4.5",
  28: "size-4",
  24: "size-3.5",
};

// solid variant 스타일 (Figma 기준)
// Primary: Primary/Base (#171717) 배경 + Static/White 텍스트
// Blue: Primary/Color (#155dfc) 배경 + Static/White 텍스트
// Disabled: opacity/24 (Figma)
const solidStyles: Record<ButtonColor, string> = {
  primary: "bg-primary-base text-static-white disabled:opacity-24",
  secondary: "bg-neutral-900 text-white disabled:opacity-24",
  blue: "bg-primary-color text-static-white disabled:opacity-24",
  // 되돌릴 수 없는 파괴적 확인 버튼 전용 (예: 태그 일괄 해제·덮어쓰기)
  negative: "bg-status-error text-static-white disabled:opacity-24",
};

// outlined variant 스타일 (Figma 기준)
// Primary: text=Primary/Color (#155dfc), border=Line/Normal
// Secondary: text=Label/Normal (#171717), border=Line/Normal
// Disabled: opacity/24
const outlinedStyles: Record<ButtonColor, string> = {
  primary: "border border-line-normal text-primary-color disabled:opacity-24",
  secondary: "border border-line-normal text-label-normal disabled:opacity-24",
  // Figma 에 outlined blue 는 따로 없다 — outlined primary 가 이미 파란 글자라 같은 스타일
  blue: "border border-line-normal text-primary-color disabled:opacity-24",
  negative: "border border-line-normal text-status-error disabled:opacity-24",
};

// 오버레이 색상 (Figma Decorate/Interaction/* 매핑)
// - solid: Decorate/Interaction/White 흰색 overlay (Strong)
// - outlined: Decorate/Interaction/{Blue|Neutral} (Light)
const overlayStyles: Record<ButtonVariant, Record<ButtonColor, string>> = {
  solid: {
    primary: "bg-white",
    secondary: "bg-white",
    blue: "bg-white",
    negative: "bg-white",
  },
  outlined: {
    primary: "bg-blue-500", // #2b7fff (Decorate/Interaction/Blue)
    secondary: "bg-neutral-950", // #0a0a0a (Decorate/Interaction/Neutral)
    blue: "bg-blue-500",
    negative: "bg-status-error",
  },
};

// State별 overlay opacity (Figma Decorate/Interaction 강도)
// - solid: Strong (hover 12% / focus 18% / pressed 24%)
// - outlined: Light (hover 6% / focus 9% / pressed 12%)
const overlayStateStyles: Record<ButtonVariant, string> = {
  solid:
    "group-hover/button:opacity-12 group-focus-visible/button:opacity-[.18] group-active/button:opacity-24",
  outlined:
    "group-hover/button:opacity-6 group-focus-visible/button:opacity-[.09] group-active/button:opacity-12",
};

// ============================================
// Button 컴포넌트
// ============================================

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "outlined",
      color = "secondary",
      size = "base",
      leftIcon,
      rightIcon,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const variantStyle =
      variant === "solid" ? solidStyles[color] : outlinedStyles[color];

    return (
      <button
        ref={ref}
        type="button"
        className={cn(baseStyles, sizeStyles[size], variantStyle, className)}
        {...props}
      >
        {/* interaction overlay: Figma Decorate/Interaction 강도 */}
        <span
          className={cn(
            "absolute inset-0 rounded-[inherit] opacity-0 transition-opacity pointer-events-none",
            overlayStateStyles[variant],
            overlayStyles[variant][color],
          )}
          aria-hidden
        />
        {leftIcon && (
          <span
            className={cn(
              "relative flex items-center justify-center",
              iconSizeStyles[size],
            )}
          >
            {leftIcon}
          </span>
        )}
        <span className={cn("relative whitespace-nowrap", textStyles[size])}>
          {children}
        </span>
        {rightIcon && (
          <span
            className={cn(
              "relative flex items-center justify-center",
              iconSizeStyles[size],
            )}
          >
            {rightIcon}
          </span>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
